import { useEffect, useRef, useCallback } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'

type EventHandler = (...args: unknown[]) => void

export type EmailAssetStreamEvent = {
    Type: 'thinking' | 'content' | 'tool_use' | 'tool_result' | 'result' | 'error' | 'done' | 'analyzing' | 'rebuilding' | 'rebuilt' | 'rebuildRejected' | 'conversationalResponse' | 'assistantMessageAppended' | 'chatStateChanged' | 'userMessageAppended' | 'token_usage'
    Content?: string
    Tool?: string
    InputTokens?: number
    OutputTokens?: number
    /**
     * Model id that produced the content of this event. Set on
     * conversationalResponse (analyzer's model) and assistantMessageAppended
     * (agent's model). Drives MessageTokenFooter on the just-appended bubble.
     */
    ModelId?: string | null
    /** Sub-classifier for token_usage events. Sparse end-of-turn snap. */
    Phase?: 'partial'
    /** Set on conversationalResponse when the orchestrator will follow up with a rebuild. */
    WillRebuild?: boolean
    /** Set on every email-asset event by the server so the dashboard can filter out
     *  foreign-asset events when the shared SignalR connection is in multiple
     *  emailasset-{id} groups (useBulkEmailGeneration scenario). */
    AssetId?: string
    /** Set on chatStateChanged (Active) and userMessageAppended so spectator
     *  dashboards can attribute the locked input and message bubble without an
     *  extra user lookup. */
    TurnUserId?: string | null
    TurnUserName?: string | null
}

type EmailAssetCallbacks = {
    onThinking?: (content: string) => void
    onContent?: (content: string) => void
    onToolUse?: (tool: string) => void
    onToolResult?: (tool: string) => void
    onResult?: (event: EmailAssetStreamEvent) => void
    onError?: (message: string) => void
    onDone?: () => void
    onAnalyzing?: () => void
    onRebuilding?: () => void
    onRebuilt?: (content: string) => void
    /** Fired when the server rejected a rebuild (e.g. an edit that would have broken a
     *  protected template slot) and kept the prior draft. Terminal like onRebuilt, but
     *  not an error — content is user-safe text and the caller should not refetch. */
    onRebuildRejected?: (content: string) => void
    onConversationalResponse?: (content: string, willRebuild: boolean, modelId: string | null, outputTokens: number | null) => void
    /** Closer bubble from AgentCostRecorder — appends a visible Webster message
     *  with the agent's model+tokens stamped so MessageTokenFooter renders
     *  immediately. */
    onAssistantMessageAppended?: (content: string, modelId: string | null, outputTokens: number | null) => void
    /** Turn-lock state change for the shared asset-chat session. Active means
     *  another user is mid-turn; Idle means the turn ended. */
    onChatStateChanged?: (state: 'Idle' | 'Active', turnUserId: string | null, turnUserName: string | null) => void
    /** Another user appended a chat message. Dedupe by createdById + content
     *  when this client is the sender. */
    onUserMessageAppended?: (content: string, createdById: string | null, createdByName: string | null) => void
}

interface UseEmailAssetSignalROptions extends EmailAssetCallbacks {
    assetId: string | undefined
}

/**
 * Manages SignalR subscription for email asset generation streaming events.
 * Joins the `emailasset-{assetId}` group and listens for `EmailAssetGenerationEvent`.
 *
 * Pattern: useLandingPageSignalR.ts — callbacks stored in refs to prevent
 * mid-generation unsubscribe/resubscribe from callback identity changes.
 */
export function useEmailAssetSignalR({
    assetId,
    onThinking,
    onContent,
    onToolUse,
    onToolResult,
    onResult,
    onError,
    onDone,
    onAnalyzing,
    onRebuilding,
    onRebuilt,
    onRebuildRejected,
    onConversationalResponse,
    onAssistantMessageAppended,
    onChatStateChanged,
    onUserMessageAppended,
}: UseEmailAssetSignalROptions) {
    const handlerRef = useRef<{ handler: EventHandler; group: string } | null>(null)

    const callbacks: EmailAssetCallbacks = {
        onThinking, onContent, onToolUse, onToolResult, onResult, onError, onDone,
        onAnalyzing, onRebuilding, onRebuilt, onRebuildRejected, onConversationalResponse,
        onAssistantMessageAppended, onChatStateChanged, onUserMessageAppended,
    }
    const callbacksRef = useRef<EmailAssetCallbacks>(callbacks)
    callbacksRef.current = callbacks

    const cleanup = useCallback(() => {
        const entry = handlerRef.current
        if (entry) {
            signalRService.off('EmailAssetGenerationEvent', entry.handler)
            signalRService.leaveGroup(entry.group)
            handlerRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!assetId) return

        const group = `emailasset-${assetId}`
        let cancelled = false

        const handleEvent: EventHandler = (payload: unknown) => {
            const raw = payload as Record<string, unknown>
            // Handle both PascalCase (C# default) and camelCase (JS SignalR default)
            const event: EmailAssetStreamEvent = {
                Type: (raw.Type ?? raw.type) as EmailAssetStreamEvent['Type'],
                Content: (raw.Content ?? raw.content) as string | undefined,
                Tool: (raw.Tool ?? raw.tool) as string | undefined,
                InputTokens: (raw.InputTokens ?? raw.inputTokens) as number | undefined,
                OutputTokens: (raw.OutputTokens ?? raw.outputTokens) as number | undefined,
                ModelId: (raw.ModelId ?? raw.modelId) as string | null | undefined,
                Phase: (raw.Phase ?? raw.phase) as 'partial' | undefined,
                WillRebuild: (raw.WillRebuild ?? raw.willRebuild) as boolean | undefined,
                AssetId: (raw.AssetId ?? raw.assetId) as string | undefined,
                TurnUserId: (raw.TurnUserId ?? raw.turnUserId) as string | null | undefined,
                TurnUserName: (raw.TurnUserName ?? raw.turnUserName) as string | null | undefined,
            }

            // Filter foreign-asset events. The shared SignalR connection is in every
            // group joined by every active hook, so events from sibling assets monitored
            // by useBulkEmailGeneration leak into this handler. Server-side every event
            // now carries AssetId; reject anything that doesn't match. If the field is
            // missing (older API), fall through to per-case content-based filters below.
            if (event.AssetId && event.AssetId !== assetId) return

            const cb = callbacksRef.current
            switch (event.Type) {
                case 'thinking':
                    cb.onThinking?.(event.Content ?? '')
                    break
                case 'content':
                    cb.onContent?.(event.Content ?? '')
                    break
                case 'tool_use':
                    cb.onToolUse?.(event.Tool ?? '')
                    break
                case 'tool_result':
                    cb.onToolResult?.(event.Tool ?? '')
                    break
                case 'result':
                    cb.onResult?.(event)
                    break
                case 'error':
                    cb.onError?.(event.Content ?? 'An error occurred during generation')
                    break
                case 'done':
                    cb.onDone?.()
                    break
                case 'analyzing':
                    cb.onAnalyzing?.()
                    break
                case 'rebuilding':
                    cb.onRebuilding?.()
                    break
                case 'rebuilt': {
                    // Filter foreign-asset completions. The shared connection
                    // event handler fires for every rebuilt event the client
                    // receives, including events from emailasset-{otherId}
                    // groups joined by useBulkEmailGeneration. Without this
                    // guard, the builder for asset A would trigger its rocket
                    // animation whenever any bulk-batch asset completes.
                    // `rebuilt` payload content is `asset:{assetId}:v{ticks}`.
                    const eventAssetId = (event.Content ?? '').split(':')[1]
                    if (eventAssetId && eventAssetId !== assetId) break
                    cb.onRebuilt?.(event.Content ?? '')
                    break
                }
                case 'rebuildRejected':
                    // Server rejected the rebuild (e.g. an edit that would have broken a
                    // protected template slot) and kept the prior draft. Terminal like
                    // 'rebuilt'/'error', but not an error — Content is user-safe text. The
                    // AssetId filter above already scopes this to our asset.
                    cb.onRebuildRejected?.(event.Content ?? '')
                    break
                case 'conversationalResponse':
                    cb.onConversationalResponse?.(
                        event.Content ?? '',
                        event.WillRebuild === true,
                        event.ModelId ?? null,
                        event.OutputTokens ?? null,
                    )
                    break
                case 'assistantMessageAppended':
                    cb.onAssistantMessageAppended?.(
                        event.Content ?? '',
                        event.ModelId ?? null,
                        event.OutputTokens ?? null,
                    )
                    break
                case 'token_usage':
                    // Sparse end-of-turn snap; consumed server-side by AgentCostRecorder.
                    // Dashboard reads per-message model+tokens from the persisted chat
                    // message via MessageTokenFooter, not from this event.
                    break
                case 'chatStateChanged':
                    cb.onChatStateChanged?.(
                        (event.Content as 'Idle' | 'Active') ?? 'Idle',
                        event.TurnUserId ?? null,
                        event.TurnUserName ?? null,
                    )
                    break
                case 'userMessageAppended':
                    cb.onUserMessageAppended?.(
                        event.Content ?? '',
                        event.TurnUserId ?? null,
                        event.TurnUserName ?? null,
                    )
                    break
            }
        }

        signalRService.joinGroup(group).then(() => {
            if (cancelled) {
                signalRService.leaveGroup(group)
                return
            }
            signalRService.on('EmailAssetGenerationEvent', handleEvent)
            handlerRef.current = { handler: handleEvent, group }
        }).catch((err) => {
            console.error('Failed to join email asset SignalR group:', err)
        })

        return () => {
            cancelled = true
            cleanup()
        }
    }, [assetId, cleanup])

    return { cleanup }
}
